import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { User } from "../App";
import "../styles/SecurityDashboard.css";

interface SecurityDashboardProps {
  user: User;
}

interface Jail {
  name: string;
  banned: number;
  total: number;
}

interface RecentBan {
  ip: string;
  jail: string;
  time: string;
}

interface FirewallRule {
  to: string;
  action: string;
  from: string;
}

interface SecurityData {
  timestamp: string;
  fail2ban: {
    active: boolean;
    jails: Jail[];
    recentBans: RecentBan[];
  };
  rkhunter: {
    installed: boolean;
    lastScan: string;
    warnings: number;
    status: string;
  };
  clamav: {
    installed: boolean;
    lastScan: string;
    threatsFound: number;
    definitionsDate: string;
    status: string;
  };
  lynis: {
    installed: boolean;
    lastScan: string;
    hardeningScore: number;
    warnings: number;
    suggestions: number;
  };
  firewall: {
    active: boolean;
    rules: FirewallRule[];
  };
  ssh: {
    permitRootLogin: string;
    passwordAuth: string;
    maxAuthTries: string;
  };
  auditd: {
    active: boolean;
    rules: number;
  };
  aide: {
    installed: boolean;
    databaseExists: boolean;
    lastCheck: string;
  };
}

type TabType = "overview" | "fail2ban" | "scans" | "firewall" | "logs";

function SecurityDashboard({ user }: SecurityDashboardProps): React.ReactElement {
  const [data, setData] = useState<SecurityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [actionLoading, setActionLoading] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [logType, setLogType] = useState("fail2ban");
  const [unbanIp, setUnbanIp] = useState("");
  const [unbanJail, setUnbanJail] = useState("");

  const fetchSecurityData = useCallback(async () => {
    try {
      setError("");
      const response = await axios.get("/api/admin/security-status");
      setData(response.data.data || response.data);
    } catch (err: any) {
      console.error("Security data error:", err);
      setError(err.response?.data?.error || "Failed to load security data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user.role === "superadmin") {
      fetchSecurityData();
    }
  }, [user, fetchSecurityData]);

  const handleRefresh = async () => {
    setLoading(true);
    await fetchSecurityData();
  };

  const handleUnban = async (ip: string, jail?: string) => {
    if (!ip) return;
    setActionLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await axios.post("/api/admin/unban", { ip, jail });
      setSuccess(response.data.message || `IP ${ip} unbanned successfully`);
      setUnbanIp("");
      setUnbanJail("");
      await fetchSecurityData();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to unban IP");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunScan = async (scanner: string) => {
    setActionLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await axios.post("/api/admin/scan", { scanner });
      setSuccess(response.data.message || `${scanner} scan started`);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to start scan");
    } finally {
      setActionLoading(false);
    }
  };

  const handleFetchLogs = async (type: string) => {
    setLogType(type);
    setActionLoading(true);
    try {
      const response = await axios.get(`/api/admin/logs/${type}?lines=100`);
      setLogs(response.data.data?.lines || []);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fetch logs");
      setLogs([]);
    } finally {
      setActionLoading(false);
    }
  };

  // Access check
  if (user.role !== "superadmin") {
    return (
      <div className="access-denied">
        <h2>Access Denied</h2>
        <p>Only superadmins can access the Security Dashboard.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="security-loading">
        <div className="security-loading-spinner"></div>
      </div>
    );
  }

  const totalBanned = data?.fail2ban.jails.reduce((sum, j) => sum + j.banned, 0) || 0;

  return (
    <div className="security-dashboard-container">
      <h2>Security Dashboard</h2>
      <p className="subtitle">
        Server security monitoring and management - Last updated: {data?.timestamp ? new Date(data.timestamp).toLocaleString() : "N/A"}
      </p>

      {error && <div className="security-error">{error}</div>}
      {success && <div className="security-success">{success}</div>}

      {/* Quick Actions */}
      <div className="security-actions">
        <button className="security-btn security-btn-primary refresh-btn" onClick={handleRefresh} disabled={loading}>
          Refresh Status
        </button>
      </div>

      {/* Tabs */}
      <div className="security-tabs">
        <button className={`security-tab ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")}>
          Overview
        </button>
        <button className={`security-tab ${activeTab === "fail2ban" ? "active" : ""}`} onClick={() => setActiveTab("fail2ban")}>
          Fail2Ban
        </button>
        <button className={`security-tab ${activeTab === "scans" ? "active" : ""}`} onClick={() => setActiveTab("scans")}>
          Security Scans
        </button>
        <button className={`security-tab ${activeTab === "firewall" ? "active" : ""}`} onClick={() => setActiveTab("firewall")}>
          Firewall & SSH
        </button>
        <button className={`security-tab ${activeTab === "logs" ? "active" : ""}`} onClick={() => setActiveTab("logs")}>
          Logs
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && data && (
        <>
          {/* Status Cards */}
          <div className="security-status-grid">
            <div className="security-status-card">
              <h4>Fail2Ban</h4>
              <div className={`value ${data.fail2ban.active ? "success" : "danger"}`}>
                {data.fail2ban.active ? "Active" : "Inactive"}
              </div>
              <div className="label">{totalBanned} IPs banned</div>
            </div>

            <div className="security-status-card">
              <h4>Firewall</h4>
              <div className={`value ${data.firewall.active ? "success" : "danger"}`}>
                {data.firewall.active ? "Active" : "Inactive"}
              </div>
              <div className="label">{data.firewall.rules.length} rules</div>
            </div>

            <div className="security-status-card">
              <h4>Lynis Score</h4>
              <div className={`value ${data.lynis.hardeningScore >= 70 ? "success" : data.lynis.hardeningScore >= 50 ? "warning" : "danger"}`}>
                {data.lynis.hardeningScore || "N/A"}
              </div>
              <div className="label">{data.lynis.warnings} warnings</div>
            </div>

            <div className="security-status-card">
              <h4>RKHunter</h4>
              <div className={`value ${data.rkhunter.status === "clean" ? "success" : "warning"}`}>
                {data.rkhunter.status === "clean" ? "Clean" : `${data.rkhunter.warnings} Warnings`}
              </div>
              <div className="label">Last: {data.rkhunter.lastScan?.split(" ")[0] || "Never"}</div>
            </div>

            <div className="security-status-card">
              <h4>ClamAV</h4>
              <div className={`value ${data.clamav.status === "clean" ? "success" : "danger"}`}>
                {data.clamav.status === "clean" ? "Clean" : `${data.clamav.threatsFound} Threats`}
              </div>
              <div className="label">Last: {data.clamav.lastScan?.split(" ")[0] || "Never"}</div>
            </div>

            <div className="security-status-card">
              <h4>Auditd</h4>
              <div className={`value ${data.auditd.active ? "success" : "danger"}`}>
                {data.auditd.active ? "Active" : "Inactive"}
              </div>
              <div className="label">{data.auditd.rules} audit rules</div>
            </div>
          </div>

          {/* SSH Configuration */}
          <div className="security-section">
            <div className="security-section-header">
              <h3>SSH Configuration</h3>
            </div>
            <div className="security-section-body">
              <div className="ssh-config-grid">
                <div className="ssh-config-item">
                  <span className="key">Root Login</span>
                  <span className={`value ${data.ssh.permitRootLogin === "no" ? "good" : "bad"}`}>
                    {data.ssh.permitRootLogin}
                  </span>
                </div>
                <div className="ssh-config-item">
                  <span className="key">Password Auth</span>
                  <span className={`value ${data.ssh.passwordAuth === "no" ? "good" : "bad"}`}>
                    {data.ssh.passwordAuth}
                  </span>
                </div>
                <div className="ssh-config-item">
                  <span className="key">Max Auth Tries</span>
                  <span className={`value ${parseInt(data.ssh.maxAuthTries) <= 3 ? "good" : "bad"}`}>
                    {data.ssh.maxAuthTries}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Bans */}
          <div className="security-section">
            <div className="security-section-header">
              <h3>Recent Bans</h3>
              <span className="status-badge active">{data.fail2ban.recentBans.length} recent</span>
            </div>
            <div className="security-section-body">
              <div className="recent-bans-list">
                {data.fail2ban.recentBans.map((ban, idx) => (
                  <div key={idx} className="ban-item">
                    <span className="ip">{ban.ip}</span>
                    <span className="jail">{ban.jail}</span>
                    <span className="time">{ban.time}</span>
                    <button
                      className="security-btn security-btn-sm security-btn-danger"
                      onClick={() => handleUnban(ban.ip, ban.jail)}
                      disabled={actionLoading}
                    >
                      Unban
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Fail2Ban Tab */}
      {activeTab === "fail2ban" && data && (
        <>
          {/* Unban Form */}
          <div className="security-section">
            <div className="security-section-header">
              <h3>Unban IP Address</h3>
            </div>
            <div className="security-section-body">
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <div className="security-form-group" style={{ flex: 1, minWidth: "200px" }}>
                  <label>IP Address</label>
                  <input
                    type="text"
                    value={unbanIp}
                    onChange={(e) => setUnbanIp(e.target.value)}
                    placeholder="e.g., 192.168.1.100"
                  />
                </div>
                <div className="security-form-group" style={{ flex: 1, minWidth: "200px" }}>
                  <label>Jail (optional)</label>
                  <select value={unbanJail} onChange={(e) => setUnbanJail(e.target.value)}>
                    <option value="">All jails</option>
                    {data.fail2ban.jails.map((jail) => (
                      <option key={jail.name} value={jail.name}>{jail.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ alignSelf: "flex-end" }}>
                  <button
                    className="security-btn security-btn-primary"
                    onClick={() => handleUnban(unbanIp, unbanJail || undefined)}
                    disabled={!unbanIp || actionLoading}
                  >
                    Unban IP
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Jails */}
          <div className="security-section">
            <div className="security-section-header">
              <h3>Fail2Ban Jails</h3>
              <span className={`status-badge ${data.fail2ban.active ? "active" : "inactive"}`}>
                {data.fail2ban.active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="security-section-body">
              <div className="jail-grid">
                {data.fail2ban.jails.map((jail) => (
                  <div key={jail.name} className="jail-card">
                    <h4>{jail.name}</h4>
                    <div className="jail-stats">
                      <span className="stat">
                        Currently: <strong>{jail.banned}</strong>
                      </span>
                      <span className="stat">
                        Total: <strong>{jail.total}</strong>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* All Recent Bans Table */}
          <div className="security-section">
            <div className="security-section-header">
              <h3>Recent Bans</h3>
            </div>
            <div className="security-section-body">
              <table className="security-table">
                <thead>
                  <tr>
                    <th>IP Address</th>
                    <th>Jail</th>
                    <th>Time</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.fail2ban.recentBans.map((ban, idx) => (
                    <tr key={idx}>
                      <td style={{ fontFamily: "monospace" }}>{ban.ip}</td>
                      <td>{ban.jail}</td>
                      <td>{ban.time}</td>
                      <td>
                        <button
                          className="security-btn security-btn-sm security-btn-danger"
                          onClick={() => handleUnban(ban.ip, ban.jail)}
                          disabled={actionLoading}
                        >
                          Unban
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Security Scans Tab */}
      {activeTab === "scans" && data && (
        <>
          <div className="security-actions">
            <button
              className="security-btn security-btn-primary"
              onClick={() => handleRunScan("rkhunter")}
              disabled={actionLoading}
            >
              Run RKHunter Scan
            </button>
            <button
              className="security-btn security-btn-primary"
              onClick={() => handleRunScan("clamav")}
              disabled={actionLoading}
            >
              Run ClamAV Scan
            </button>
            <button
              className="security-btn security-btn-primary"
              onClick={() => handleRunScan("lynis")}
              disabled={actionLoading}
            >
              Run Lynis Audit
            </button>
          </div>

          <div className="security-two-column">
            {/* Lynis */}
            <div className="security-section">
              <div className="security-section-header">
                <h3>Lynis Security Audit</h3>
                <span className={`status-badge ${data.lynis.installed ? "active" : "inactive"}`}>
                  {data.lynis.installed ? "Installed" : "Not Installed"}
                </span>
              </div>
              <div className="security-section-body">
                <div className="score-display">
                  <div className={`score-circle ${data.lynis.hardeningScore >= 70 ? "high" : data.lynis.hardeningScore >= 50 ? "medium" : "low"}`}>
                    {data.lynis.hardeningScore}
                  </div>
                  <div>
                    <p style={{ margin: 0, fontWeight: 600 }}>Hardening Score</p>
                    <p style={{ margin: "5px 0", color: "#718096" }}>
                      {data.lynis.warnings} warnings, {data.lynis.suggestions} suggestions
                    </p>
                    <p style={{ margin: 0, fontSize: "12px", color: "#a0aec0" }}>
                      Last scan: {data.lynis.lastScan || "Never"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* RKHunter */}
            <div className="security-section">
              <div className="security-section-header">
                <h3>RKHunter (Rootkit Detection)</h3>
                <span className={`status-badge ${data.rkhunter.status === "clean" ? "active" : "warning"}`}>
                  {data.rkhunter.status === "clean" ? "Clean" : "Warnings"}
                </span>
              </div>
              <div className="security-section-body">
                <div className="ssh-config-grid">
                  <div className="ssh-config-item">
                    <span className="key">Status</span>
                    <span className={`value ${data.rkhunter.status === "clean" ? "good" : "bad"}`}>
                      {data.rkhunter.status}
                    </span>
                  </div>
                  <div className="ssh-config-item">
                    <span className="key">Warnings</span>
                    <span className={`value ${data.rkhunter.warnings === 0 ? "good" : "bad"}`}>
                      {data.rkhunter.warnings}
                    </span>
                  </div>
                  <div className="ssh-config-item">
                    <span className="key">Last Scan</span>
                    <span className="value">{data.rkhunter.lastScan || "Never"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ClamAV */}
            <div className="security-section">
              <div className="security-section-header">
                <h3>ClamAV (Malware Scanner)</h3>
                <span className={`status-badge ${data.clamav.status === "clean" ? "active" : "warning"}`}>
                  {data.clamav.status === "clean" ? "Clean" : "Threats Found"}
                </span>
              </div>
              <div className="security-section-body">
                <div className="ssh-config-grid">
                  <div className="ssh-config-item">
                    <span className="key">Status</span>
                    <span className={`value ${data.clamav.status === "clean" ? "good" : "bad"}`}>
                      {data.clamav.status}
                    </span>
                  </div>
                  <div className="ssh-config-item">
                    <span className="key">Threats Found</span>
                    <span className={`value ${data.clamav.threatsFound === 0 ? "good" : "bad"}`}>
                      {data.clamav.threatsFound}
                    </span>
                  </div>
                  <div className="ssh-config-item">
                    <span className="key">Last Scan</span>
                    <span className="value">{data.clamav.lastScan || "Never"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* AIDE */}
            <div className="security-section">
              <div className="security-section-header">
                <h3>AIDE (File Integrity)</h3>
                <span className={`status-badge ${data.aide.installed ? "active" : "inactive"}`}>
                  {data.aide.installed ? "Installed" : "Not Installed"}
                </span>
              </div>
              <div className="security-section-body">
                <div className="ssh-config-grid">
                  <div className="ssh-config-item">
                    <span className="key">Database</span>
                    <span className={`value ${data.aide.databaseExists ? "good" : "bad"}`}>
                      {data.aide.databaseExists ? "Exists" : "Missing"}
                    </span>
                  </div>
                  <div className="ssh-config-item">
                    <span className="key">Last Check</span>
                    <span className="value">{data.aide.lastCheck || "Never"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Firewall & SSH Tab */}
      {activeTab === "firewall" && data && (
        <>
          {/* Firewall Rules */}
          <div className="security-section">
            <div className="security-section-header">
              <h3>UFW Firewall Rules</h3>
              <span className={`status-badge ${data.firewall.active ? "active" : "inactive"}`}>
                {data.firewall.active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="security-section-body">
              <table className="security-table">
                <thead>
                  <tr>
                    <th>To</th>
                    <th>Action</th>
                    <th>From</th>
                  </tr>
                </thead>
                <tbody>
                  {data.firewall.rules.map((rule, idx) => (
                    <tr key={idx}>
                      <td>{rule.to}</td>
                      <td>
                        <span className={`status-badge ${rule.action === "ALLOW" ? "active" : rule.action === "DENY" ? "inactive" : "warning"}`}>
                          {rule.action}
                        </span>
                      </td>
                      <td>{rule.from}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* SSH Configuration */}
          <div className="security-section">
            <div className="security-section-header">
              <h3>SSH Configuration</h3>
            </div>
            <div className="security-section-body">
              <div className="ssh-config-grid">
                <div className="ssh-config-item">
                  <span className="key">Permit Root Login</span>
                  <span className={`value ${data.ssh.permitRootLogin === "no" ? "good" : "bad"}`}>
                    {data.ssh.permitRootLogin}
                  </span>
                </div>
                <div className="ssh-config-item">
                  <span className="key">Password Authentication</span>
                  <span className={`value ${data.ssh.passwordAuth === "no" ? "good" : "bad"}`}>
                    {data.ssh.passwordAuth}
                  </span>
                </div>
                <div className="ssh-config-item">
                  <span className="key">Max Auth Tries</span>
                  <span className={`value ${parseInt(data.ssh.maxAuthTries) <= 3 ? "good" : "bad"}`}>
                    {data.ssh.maxAuthTries}
                  </span>
                </div>
              </div>
              {data.ssh.permitRootLogin === "yes" && (
                <div className="security-error" style={{ marginTop: "15px" }}>
                  Warning: Root login is enabled. Consider disabling it for better security.
                </div>
              )}
            </div>
          </div>

          {/* Auditd */}
          <div className="security-section">
            <div className="security-section-header">
              <h3>Auditd (System Auditing)</h3>
              <span className={`status-badge ${data.auditd.active ? "active" : "inactive"}`}>
                {data.auditd.active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="security-section-body">
              <div className="ssh-config-grid">
                <div className="ssh-config-item">
                  <span className="key">Status</span>
                  <span className={`value ${data.auditd.active ? "good" : "bad"}`}>
                    {data.auditd.active ? "Running" : "Stopped"}
                  </span>
                </div>
                <div className="ssh-config-item">
                  <span className="key">Audit Rules</span>
                  <span className="value">{data.auditd.rules}</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Logs Tab */}
      {activeTab === "logs" && (
        <>
          <div className="security-actions">
            <button
              className={`security-btn ${logType === "fail2ban" ? "security-btn-primary" : ""}`}
              onClick={() => handleFetchLogs("fail2ban")}
              disabled={actionLoading}
            >
              Fail2Ban Logs
            </button>
            <button
              className={`security-btn ${logType === "auth" ? "security-btn-primary" : ""}`}
              onClick={() => handleFetchLogs("auth")}
              disabled={actionLoading}
            >
              Auth Logs
            </button>
            <button
              className={`security-btn ${logType === "nginx" ? "security-btn-primary" : ""}`}
              onClick={() => handleFetchLogs("nginx")}
              disabled={actionLoading}
            >
              Nginx Error Logs
            </button>
            <button
              className={`security-btn ${logType === "syslog" ? "security-btn-primary" : ""}`}
              onClick={() => handleFetchLogs("syslog")}
              disabled={actionLoading}
            >
              Syslog
            </button>
          </div>

          <div className="security-section">
            <div className="security-section-header">
              <h3>{logType.charAt(0).toUpperCase() + logType.slice(1)} Logs</h3>
              <span className="label">{logs.length} lines</span>
            </div>
            <div className="security-section-body">
              {logs.length > 0 ? (
                <div className="logs-viewer">
                  {logs.map((line, idx) => (
                    <div key={idx} className="log-line">{line}</div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "#718096", textAlign: "center", padding: "20px" }}>
                  Click a log type button above to view logs
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SecurityDashboard;
